import React, { useState, useRef, Fragment } from "react";
import { put, post } from "util/requests";
import styles from "../crf_builder.module.css";
import { Select, Checkbox } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import { cloneDeep } from "lodash";
import AddIcon from "@mui/icons-material/Add";
import EditableText from "./editable_text";

function CalculationRow(props) {
  const { setDisplayIfLogic, calculation, rule_index, field } = props;
  let options = [{ label: "Add Field", value: "add_field" }];
  if (rule_index === 0) {
    options.unshift({ label: "Initial Value", value: "initial_value" });
  }
  return (
    <div className={styles.flex_row}>
      <Select
        value={calculation.calc_type || ""}
        sx={{
          fontSize: 14,
          minWidth: 200,
          height: 30,
          padding: 0,
          alignSelf: "center",
        }}
        onChange={(e) => setDisplayIfLogic(field.id, rule_index, "calc_type", e.target.value)}
      >
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </Select>

      <EditableText
        text={calculation.value || ""}
        placeholder={"value"}
        onChange={(value) => {
          setDisplayIfLogic(field.id, rule_index, "value", value);
        }}
      />
    </div>
  );
}
function Calculation(props) {
  const { field, setExcludeActualOrEstimate, updateField } = props;

  const _setDisplayIfLogic = (field_id, rule_index, key, value) => {
    let calculation = field.calculation || [];
    calculation[rule_index][key] = value;

    updateField(field_id, field);
  };

  const addCalcRow = (field_id) => {
    if (!field.calculation) {
      field.calculation = [];
    }
    field.calculation.push({ field_id: "", calc_type: "", value: "" });
    updateField(field_id, field);
  };

  return (
    <div>
      <div>Calculation:</div>
      <div>
        {field.calculation?.map((calc, calc_index) => (
          <CalculationRow
            field={field}
            rule_index={calc_index}
            setDisplayIfLogic={_setDisplayIfLogic}
            calculation={calc}
          />
        ))}
        <AddIcon onClick={() => addCalcRow(field.id)} />
      </div>
    </div>
  );
}

function ExcludeActualOrEstimate(props) {
  const { field, setExcludeActualOrEstimate } = props;
  return (
    <div className={styles.read_only_row} key={`${field.key}-${field.id}`}>
      <div>Exclude Estimate Option:</div>
      <div>
        <Checkbox
          checked={field && field.exclude_actual_or_estimate}
          onChange={(e) => {
            setExcludeActualOrEstimate(e.target.checked);
          }}
          size="small"
        />
      </div>
    </div>
  );
}

function ReadOnly(props) {
  const { field, setReadOnly } = props;
  let key = crypto.randomUUID();
  return (
    <div className={styles.read_only_row} key={`${field.key}-${field.id}`}>
      <div>Read Only:</div>
      <div>
        <Checkbox
          checked={(field && field.read_only) || false}
          onChange={(e) => {
            setReadOnly(field.id, e.target.checked);
          }}
          size="small"
        />
      </div>
    </div>
  );
}

function CalculatedSource(props) {
  const { field, setCalculatedSource } = props;

  return (
    <div className={styles.read_only_row} key={`${field.key}-${field.id}`}>
      <div>Calculated Source Field:</div>
      <div>
        <Checkbox
          checked={(field && field.calculated_source) || false}
          onChange={(e) => {
            setCalculatedSource(e.target.checked);
          }}
          size="small"
        />
      </div>
    </div>
  );
}

function VersioningBasedOnField(props) {
  const { field, setVersioningBasedOnField, setVersioningFieldId } = props;

  return (
    <div className={styles.read_only_row} key={`${field.key}-${field.id}`}>
      <div>Enable Versioning Based on Field:</div>
      <div className={styles.flex_row}>
        <Checkbox
          checked={(field && field.versioning_field) || false}
          onChange={(e) => {
            setVersioningBasedOnField(e.target.checked);
          }}
          size="small"
        />
        <EditableText
          text={field?.versioning_field_id}
          placeholder={"value"}
          onChange={(value) => {
            setVersioningFieldId(value);
          }}
        />
      </div>
    </div>
  );
}

function CalculatedField(props) {
  const { field, setCalculatedField, setCalculatedRequireAllDataField } = props;

  return (
    <React.Fragment>
      <div className={styles.read_only_row} key={`${field.key}-${field.id}`}>
        <div>Calculated Field:</div>
        <div>
          <Checkbox
            checked={(field && field.calculated) || false}
            onChange={(e) => {
              setCalculatedField(e.target.checked);
            }}
            size="small"
          />
        </div>
      </div>
      <div className={styles.read_only_row} key={`${field.key}-${field.id}`}>
        <div>Calculate only if all data is filled:</div>
        <div>
          <Checkbox
            checked={(field && field.calculate_require_all_data) || false}
            onChange={(e) => {
              setCalculatedRequireAllDataField(e.target.checked);
            }}
            size="small"
          />
        </div>
      </div>
    </React.Fragment>
  );
}

function Required(props) {
  const { field, setRequired } = props;
  return (
    <div className={styles.read_only_row} key={`${field.key}-${field.id}`}>
      <div>Required:</div>
      <div>
        <Checkbox
          checked={field && field.required}
          onChange={(e) => {
            setRequired(field.id, e.target.checked);
          }}
          size="small"
        />
      </div>
    </div>
  );
}

export default function Config(props) {
  const { field, setMainCrfOptions, updateField, setReadOnly, setRequired } = props;
  const [options, setOptions] = useState(field.options || []);

  const setFieldValue = (key, value) => {
    let _field = cloneDeep(field);
    _field[key] = value;
    updateField(_field.id, _field);
  };
  const setMultipleValues = (obj) => {
    let _field = cloneDeep(field);
    _field = Object.assign(_field, obj);
    updateField(_field.id, _field);
  };

  const setPosition = (position) => {
    let _field = cloneDeep(field);
    _field.position = position;
    updateField(_field.id, _field);
  };

  if (["radio", "select"].includes(field.type)) {
    return (
      <div>
        <div>
          <div>Options Orientation</div>
          <Select
            value={field.orientation || ""}
            sx={{
              fontSize: 14,
              minWidth: 200,
              height: 30,
              padding: 0,
              alignSelf: "center",
            }}
            onChange={(e) => setFieldValue("orientation", e.target.value)}
          >
            {["horizontal", "vertical"].map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </Select>
        </div>
        <Required field={field} setRequired={setRequired} />
        <ReadOnly field={field} setReadOnly={setReadOnly} />
        <CalculatedSource field={field} setCalculatedSource={(value) => setFieldValue("calculated_source", value)} />
        <VersioningBasedOnField
          field={field}
          setVersioningBasedOnField={(value) => {
            if (value) {
              setFieldValue("versioning_field", value);
            } else {
              setMultipleValues({ versioning_field: value, versioning_field_id: "" });
            }
          }}
          setVersioningFieldId={(value) => {
            setFieldValue("versioning_field_id", value);
          }}
        />
      </div>
    );
  } else if (field.type === "heading") {
    return (
      <div>
        <div>
          <div>Position</div>
          <Select
            value={field.position || ""}
            sx={{
              fontSize: 14,
              minWidth: 200,
              height: 30,
              padding: 0,
              alignSelf: "center",
            }}
            onChange={(e) => setPosition(e.target.value)}
          >
            {["left", "center"].map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className={styles.vertical_meta_option}>
          <div>Color</div>
          <div>
            <Select
              value={field.color || ""}
              sx={{
                fontSize: 14,
                minWidth: 200,
                height: 30,
                padding: 0,
                alignSelf: "center",
              }}
              onChange={(e) => setFieldValue("color", e.target.value)}
            >
              {["black", "red"].map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>
      </div>
    );
  } else if (["weight"].includes(field.type)) {
    return (
      <div>
        <Required field={field} setRequired={setRequired} />
        <ReadOnly field={field} setReadOnly={setReadOnly} />
        <ExcludeActualOrEstimate
          field={field}
          setExcludeActualOrEstimate={(value) => setFieldValue("exclude_actual_or_estimate", value)}
        />
      </div>
    );
  } else if (["int"].includes(field.type)) {
    return (
      <div>
        <Required field={field} setRequired={setRequired} />
        <ReadOnly field={field} setReadOnly={setReadOnly} />
        <CalculatedField
          field={field}
          setCalculatedField={(value) => setFieldValue("calculated", value)}
          setCalculatedRequireAllDataField={(value) => setFieldValue("calculate_require_all_data", value)}
        />
        {field.calculated ? <Calculation field={field} updateField={updateField} /> : null}
        <VersioningBasedOnField
          field={field}
          setVersioningBasedOnField={(value) => {
            if (value) {
              setFieldValue("versioning_field", value);
            } else {
              setMultipleValues({ versioning_field: value, versioning_field_id: "" });
            }
          }}
          setVersioningFieldId={(value) => {
            setFieldValue("versioning_field_id", value);
          }}
        />
      </div>
    );
  } else if (field.type === "blood_pressure_date_time") {
    return (
      <div>
        <Required field={field} setRequired={setRequired} />
        <ReadOnly field={field} setReadOnly={setReadOnly} />
      </div>
    );
  } else if (
    ["year", "datetime", "int", "blood_test_result", "decimal", "text", "time", "date", "blood_pressure"].includes(
      field.type
    )
  ) {
    return (
      <div>
        <Required field={field} setRequired={setRequired} />
        <ReadOnly field={field} setReadOnly={setReadOnly} />
      </div>
    );
  }
}
